import { nanoid } from "nanoid";
import type React from "react";
import { createContext, type ReactNode, useContext, useState } from "react";
import type { MosaicNode } from "react-mosaic-component";
import { DEFAULT_PANEL_MODE } from "@/components/mosaic/mosaicMenu/MosaicMenuItems";
import { initialChartState } from "@/components/widgets/Chart/const";
import { initialPeadValues } from "@/components/widgets/Pead/consts";
import type { ViewId } from "@/shared/types/common";
import { useTabs } from "../tabs/TabsContext";
import { useReplaceChatWindow } from "./hooks/useReplaceChatWindow";
export interface MosaicContextProps {
	addMosaicWindow: (viewId: ViewId) => void;
	removeMosaicWindow: (viewId: ViewId) => void;
	generateUniqueId: (baseId: string) => ViewId;
	counter: number;
	setCounter: React.Dispatch<React.SetStateAction<number>>;
	isMonitor: (baseId: string) => boolean;
	clickInfo: Record<string, string>;
	setClickInfo: (v: Record<string, string>) => void;
}

const MosaicContext = createContext<MosaicContextProps | undefined>(undefined);

export const initialMosaicState: MosaicNode<ViewId> = {
	direction: "column",
	first: {
		direction: "row",
		first: "Monitor",
		second: "Chart",
	},
	second: "Screener",
};

export const MosaicProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { getTabLayout, setTabLayout, getWidgetState, setWidgetState } =
		useTabs();
	const [counter, setCounter] = useState(0);
	const [clickInfo, setClickInfo] = useState<Record<string, string>>({});

	const tabLayout = getTabLayout();

	const generateUniqueId = (baseId: string) =>
		`${baseId}-${nanoid()}` as ViewId;

	const addMosaicWindow = (viewId: ViewId) => {
		const uniqueId = generateUniqueId(viewId);

		const newNode: MosaicNode<ViewId> = uniqueId;

		if (viewId.startsWith("Drift Forecast")) {
			const peadState = getWidgetState(uniqueId) || {};
			setWidgetState(uniqueId, { ...peadState, ...initialPeadValues });
		}
		if (viewId.startsWith("Chart")) {
			const chartState = getWidgetState(uniqueId) || {};
			setWidgetState(uniqueId, { ...chartState, ...initialChartState });
		}
		if (viewId.startsWith("Monitor") || viewId.startsWith("Screener")) {
			const state = getWidgetState(uniqueId);
			setWidgetState(uniqueId, {
				...state,
				isOneClickTrigger: true,
				panelMode: DEFAULT_PANEL_MODE,
			});
		}

		if (tabLayout === null) {
			const updatedState = newNode;
			setTabLayout(updatedState);
			setCounter(counter + 1);
			return;
		}

		const addNodeToTree = (
			node: MosaicNode<ViewId>,
			count: number,
		): MosaicNode<ViewId> => {
			if (viewId.startsWith("Drift Forecast")) {
				return {
					direction: "row",
					first: node,
					second: newNode,
					splitPercentage: 75,
				};
			}

			if (viewId.startsWith("Chart")) {
				return {
					direction: "row",
					first: node,
					second: newNode,
				};
			}

			if (viewId.startsWith("Quote") || viewId.startsWith("Advanced Quote")) {
				return {
					direction: "row",
					first: node,
					second: newNode,
					splitPercentage: 75,
				};
			}

			if (typeof node === "string") {
				return {
					direction: "row",
					first: node,
					second: newNode,
				};
			}

			if (
				viewId === "Chat" &&
				tabLayout !== null &&
				typeof tabLayout === "object"
			) {
				return {
					direction: "row",
					first: {
						...tabLayout,
					},
					second: newNode,
					splitPercentage: 80,
				};
			}

			if (node.second === "Screener") {
				if (count % 2 === 0) {
					return {
						direction: "column",
						first: {
							direction: "row",
							first: node.first,
							second: newNode,
						},
						second: node.second,
					};
				}
				return {
					direction: "column",
					first: {
						direction: "column",
						first: node.first,
						second: newNode,
					},
					second: node.second,
				};
			}

			if (count % 2 === 0) {
				return {
					...node,
					second: addNodeToTree(node.second as MosaicNode<ViewId>, count + 1),
				};
			}

			return {
				direction: "column",
				first: node,
				second: newNode,
			};
		};

		const updatedState = addNodeToTree(tabLayout, counter);
		setTabLayout(updatedState);
		setCounter(counter + 1);
	};

	const removeMosaicWindow = (viewId: ViewId) => {
		const tabState = getTabLayout();

		if (!tabState) return;

		const removeNodeFromTree = (
			node: MosaicNode<ViewId>,
		): MosaicNode<ViewId> | null => {
			if (typeof node === "string") {
				return node === viewId ? null : node;
			}

			const updatedFirst = removeNodeFromTree(node.first);
			const updatedSecond = removeNodeFromTree(node.second);

			// If both children are removed, remove this parent too
			if (updatedFirst === null && updatedSecond === null) {
				return null;
			}

			// If one child is removed, return the other child
			if (updatedFirst === null) {
				return updatedSecond;
			}

			if (updatedSecond === null) {
				return updatedFirst;
			}

			// Both children exist, return the updated parent
			return {
				...node,
				first: updatedFirst,
				second: updatedSecond,
			};
		};

		const updatedState = removeNodeFromTree(tabState);
		setTabLayout(updatedState);
		setCounter(counter - 1);

		return updatedState;
	};

	const isMonitor = (baseId: string) => {
		return baseId === "Monitor";
	};

	useReplaceChatWindow(removeMosaicWindow);

	return (
		<MosaicContext.Provider
			value={{
				addMosaicWindow,
				removeMosaicWindow,
				counter,
				setCounter,
				generateUniqueId,
				isMonitor,
				clickInfo,
				setClickInfo,
			}}
		>
			{children}
		</MosaicContext.Provider>
	);
};

export const useMosaic = () => {
	const context = useContext(MosaicContext);

	if (!context) {
		throw new Error("useMosaic must be used within a MosaicProvider");
	}

	return context;
};
